import {useQuery} from "@apollo/client";
import {useFormContext} from "react-hook-form";

import {Sorting, SortingDirection} from "@/components/table/Table.types.ts";
import {ServiceField} from "@/lib/constants/service.ts";
import {useTable} from "@/lib/hooks/useTable.ts";
import {GQL_MAINTENANCE_SCHEDULES_LIST, MaintenanceListResponse} from "@/lib/queries/maintenance.ts";
import {MaintenanceSchedule, MaintenanceScheduleType} from "@/lib/types/maintenance.ts";
import {CheckLinkedSchedulesTable} from "@/pages/checks/update/sections/maintenance/CheckLinkedSchedulesTable.tsx";
import {
  getNextOccurrence,
  getScheduleState,
} from "@/pages/maintenance/maintenance-dashboard/tabs/schedules/MaintenanceSchedulesTable/MaintenanceSchedulesTable.utils.ts";

const _fieldGetFn = (schedule: MaintenanceSchedule, sorting: Sorting) => {
  if (sorting.column === "name") {
    return schedule.name;
  } else if (sorting.column === "type") {
    return schedule.schedule_type;
  } else if (sorting.column === "status") {
    return getScheduleState(schedule);
  } else if (sorting.column === "duration_minutes") {
    return schedule.duration_minutes;
  } else if (sorting.column === "next_occurrence") {
    const next_occurrence = getNextOccurrence(schedule);
    if (!next_occurrence) {
      // least possible date
      return new Date(-8640000000000000).toISOString();
    }
    return schedule.starts_at;
  }

  return schedule.id;
};

const _scheduleSortFn = (a: MaintenanceSchedule, b: MaintenanceSchedule, sorting: Sorting) => {
  const fieldA = _fieldGetFn(a, sorting);
  const fieldB = _fieldGetFn(b, sorting);
  const multiplier = sorting.direction == SortingDirection.Asc ? 1 : -1;
  if (fieldA === undefined && fieldB === undefined) return 0;
  if (fieldB === undefined) return 1 * multiplier;
  if (fieldA === undefined) return -1 * multiplier;
  if (fieldA > fieldB) {
    return 1 * multiplier;
  } else if (fieldA < fieldB) {
    return -1 * multiplier;
  }
  return 0;
};

export const CheckLinkedSchedulesList = () => {
  const {watch} = useFormContext();
  const maintenanceScheduleIds: number[] = watch(ServiceField.MAINTENANCE_SCHEDULE_IDS) || [];
  const {data, previousData, loading} = useQuery<MaintenanceListResponse>(GQL_MAINTENANCE_SCHEDULES_LIST, {
    variables: {
      limit: maintenanceScheduleIds.length,
      filters: {
        ids: maintenanceScheduleIds.map(String),
        types: [MaintenanceScheduleType.RRULE, MaintenanceScheduleType.ONE_OFF],
      },
    },
    skip: maintenanceScheduleIds.length === 0,
  });
  const {sorting, setSorting} = useTable({useUrlParams: false});
  const selectedSchedules = ((data?.response || previousData?.response)?.items || [])
    .filter((schedule) => maintenanceScheduleIds.includes(schedule.id))
    .sort((a, b) => _scheduleSortFn(a, b, sorting));
  return (
    <CheckLinkedSchedulesTable
      items={selectedSchedules}
      loading={loading}
      sortColumn={sorting.column}
      sortDirection={sorting.direction}
      onSortingChange={setSorting}
    />
  );
};
